import type { PortalSiteBootstrapResponseDto } from '@supply-mover-2-0/shared';
import { create } from 'zustand';

/**
 * Portal Site (brand) store
 *
 * Holds the active `portal_site` metadata resolved on app startup via
 * `GET /api/portal/site`. The bootstrap happens before login because the
 * storefront needs to render the brand name / logo up front, and because
 * `portalSiteId` has to be threaded into the login request so the login
 * server can enforce brand scope on the returned clients.
 *
 * For custom-domain requests this is the only way the frontend learns the
 * brand — the browser hostname isn't parseable for those.
 */
interface PortalSiteState {
	/** Null while the bootstrap request is in flight or hasn't happened yet. */
	site: PortalSiteBootstrapResponseDto | null;
	/** True after bootstrap has completed (success or failure). */
	isResolved: boolean;
	/** True when the bootstrap endpoint returned 404 — tenant/brand unconfigured. */
	isNotConfigured: boolean;

	setSite: (site: PortalSiteBootstrapResponseDto | null) => void;
	setNotConfigured: () => void;
	reset: () => void;
}

/**
 * Dev override for the login background. Lets us iterate on a new
 * brand image without round-tripping through the CDN admin upload
 * flow. Setting `VITE_DEV_LOGIN_BG=/dev-login-bg.jpg` (or any URL)
 * in `.env.development` swaps the bootstrap-returned URL in place.
 * The env var is only loaded from `.env.development`, so real
 * staging/production builds never see it.
 */
const devLoginBgOverride = import.meta.env.VITE_DEV_LOGIN_BG as
	| string
	| undefined;

export const usePortalSiteStore = create<PortalSiteState>((set) => ({
	site: null,
	isResolved: false,
	isNotConfigured: false,

	setSite: (site) => {
		const overridden =
			site != null && devLoginBgOverride != null && devLoginBgOverride !== ''
				? { ...site, loginBackgroundUrl: devLoginBgOverride }
				: site;
		set({ site: overridden, isResolved: true, isNotConfigured: site == null });
	},
	setNotConfigured: () =>
		set({ site: null, isResolved: true, isNotConfigured: true }),
	reset: () => set({ site: null, isResolved: false, isNotConfigured: false }),
}));
